import { ArrowUpRight, FileSearch, FileCheck2, MessageSquareText, BadgeCheck, Star } from "lucide-react";
import { MaskLines, Reveal } from "@/components/site/Motion";
import { useEnquiry } from "@/context/EnquiryContext";
import { IMG } from "@/data/site";
import { EImg, T } from "@/context/ContentContext";

const STEPS = [
  { icon: FileSearch, title: "Refusal history reviewed", text: "We study why earlier applications were refused before anything is filed again." },
  { icon: FileCheck2, title: "Documents fixed", text: "Clear guidance on every document so your file is complete and consistent." },
  { icon: MessageSquareText, title: "Regular updates", text: "Clear communication and status updates until a decision arrives." },
  { icon: BadgeCheck, title: "Visa approved", text: "Applications prepared properly, with the right guidance at every step." },
];

export function RefusalStory() {
  const { openEnquiry } = useEnquiry();
  return (
    <section className="relative bg-ink text-cream overflow-hidden grain py-24 sm:py-32" data-testid="refusal-story-section">
      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-6">
            <Reveal><T k="refusal.eyebrow" as="p" className="eyebrow text-blush mb-5">Success story · Visa approved after refusal</T></Reveal>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-[-0.03em] leading-[0.95]">
              <MaskLines inView delay={0} lines={[<T k="refusal.title.0">Refused twice.</T>, <T k="refusal.title.1" className="font-serif italic font-normal text-gold">Approved with us.</T>]} />
            </h2>
            <Reveal delay={0.1}>
              <T k="refusal.intro" as="p" className="mt-6 text-lg text-cream/80 max-w-lg">A Georgia visa refusal isn't the end of the road. Here's what happened when Manisha came to us after two refusals.</T>
            </Reveal>
            <Reveal delay={0.15} className="mt-10 relative rounded-[28px] bg-wine p-7 sm:p-9" data-testid="refusal-story-quote">
              <span className="flex gap-0.5">{[0, 1, 2, 3, 4].map((i) => <Star key={i} className="h-4 w-4 text-gold fill-gold" />)}</span>
              <T k="refusal.quote" as="p" className="mt-5 font-serif text-2xl sm:text-[1.75rem] leading-snug">"I had already faced two Georgia visa refusals, so I was quite doubtful about applying again… This time, my Georgia visa was successfully approved. 🙌"</T>
              <div className="mt-6 flex items-center justify-between gap-3">
                <p className="font-display font-bold"><T k="refusal.name">Manisha Kumari</T> <span className="font-body font-normal text-cream/70 text-sm">· <T k="refusal.source">Google review</T></span></p>
                <T k="refusal.tag" className="eyebrow rounded-full bg-cream/15 px-3 py-1">Georgia</T>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal className="relative arch overflow-hidden aspect-[16/11] sm:aspect-[16/10] mb-8">
              <EImg place="bottom-4 right-4" k="refusal.img" src={IMG.tbilisiOld} alt="Old Tbilisi, Georgia" loading="lazy" className="h-full w-full object-cover" />
            </Reveal>
            <ol className="grid sm:grid-cols-2 gap-4" data-testid="refusal-story-steps">
              {STEPS.map(({ icon: Icon, title, text }, i) => (
                <Reveal as="li" key={i} delay={i * 0.08}
                  className={`rounded-3xl border p-5 ${i === 3 ? "bg-gold text-ink border-gold" : "border-cream/15"}`}>
                  <div className="flex items-center justify-between">
                    <Icon className={`h-6 w-6 ${i === 3 ? "text-ink" : "text-blush"}`} />
                    <span className={`font-mono text-xs ${i === 3 ? "text-ink/70" : "text-cream/60"}`}>0{i + 1}</span>
                  </div>
                  <T k={`refusal.step.${i}.title`} as="p" className="mt-4 font-display text-lg font-bold">{title}</T>
                  <T k={`refusal.step.${i}.text`} as="p" className={`mt-1 text-sm ${i === 3 ? "text-ink/80" : "text-cream/75"}`}>{text}</T>
                </Reveal>
              ))}
            </ol>
            <Reveal delay={0.2} className="mt-8 flex flex-col gap-4">
              <button onClick={() => openEnquiry({ destination: "Georgia", trip_title: "Georgia visa: previous refusal review" })} data-testid="refusal-story-cta"
                className="group self-stretch sm:self-start whitespace-nowrap inline-flex items-center justify-between gap-4 rounded-full bg-terra text-paper pl-6 pr-2 py-2 font-semibold hover:bg-[#a5370a] transition-colors">
                <T k="refusal.cta">Get my refusal case reviewed</T>
                <span className="grid place-items-center h-10 w-10 shrink-0 rounded-full bg-paper text-terra transition-transform group-hover:rotate-45"><ArrowUpRight className="h-5 w-5" /></span>
              </button>
              <T k="refusal.note" as="p" className="text-sm text-cream/70">Visa decisions are made by the Georgian authorities. We make sure your application is as strong as it can be.</T>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
